import {ColDef} from 'ag-grid-community';
import {forEach, isEmpty, keys, map, omit} from 'lodash';

import {
  CELL_RENDERER_PARAMS,
  CELL_RENDERER_PARAMS_AG,
} from 'src/js/model/cell_renderer_params';
import {
  COLUMN_AUTO_HEIGHT,
  COLUMN_CHILDREN,
  COLUMN_COMPARATOR,
  COLUMN_HIDE,
  COLUMN_KEY,
  COLUMN_KEY_AG,
  COLUMN_NAME,
  COLUMN_NAME_AG,
  COLUMN_PROPERTY_TYPE,
  COLUMN_RENDERER_AG,
  COLUMN_SORTABLE,
  COLUMN_TOOLTIP,
  COLUMN_TOOLTIP_AG,
  COLUMN_WIDTH,
  ColumnConfig,
  TABLE_TYPE_AG,
  TABLE_TYPE_RDG,
  TableType,
} from 'src/js/model/column_config';
import {DynamicFormatter} from 'src/js/model/formatters_definitions';

const COLUMN_FORMATTER = 'formatter';
const COLUMN_FORMATTER_FUNCTION = 'formatterFunction';
const COLUMN_PINNED_FORMATTER = 'pinnedFormatter';
const COLUMN_GET_ROW_META_DATA = 'getRowMetaData';
const COLUMN_HEADER_FORMATTER = 'headerFormatter';

const COLUMN_SORTABLE_AG = 'sortable';
export const COLUMN_WIDTH_AG = 'width';
const COLUMN_FORMATTER_AG = 'cellRendererFramework';
const COLUMN_HEADER_FORMATTER_AG = 'headerComponentFramework';
const COLUMN_PINNED_FORMATTER_AG = 'pinnedRowCellRendererFramework';
const COLUMN_FILTER = 'filterFramework';
const COLUMN_FILTER_PARAMS = 'filterParams';
const COLUMN_FILTER_TEXT_COMPARATOR = 'textCustomComparator';
const COLUMN_PROPERTY_TYPE_AG = 'propertyType';

const DEFAULT_COLUMN_PROPERTIES = {
  [TABLE_TYPE_RDG]: {
    resizable: true,
  },
  [TABLE_TYPE_AG]: {},
};

// TODO: RDG is not used anymore clean this
const TABLE_TYPE_DEFINITION = {
  [TABLE_TYPE_RDG]: {
    [COLUMN_KEY]: [COLUMN_KEY],
    [COLUMN_NAME]: [COLUMN_NAME],
    [COLUMN_TOOLTIP]: [COLUMN_TOOLTIP],
    [COLUMN_SORTABLE]: [COLUMN_SORTABLE],
    [COLUMN_HIDE]: [COLUMN_HIDE],
    [COLUMN_WIDTH]: [COLUMN_WIDTH],
    [COLUMN_FORMATTER]: [COLUMN_FORMATTER],
    [CELL_RENDERER_PARAMS]: [CELL_RENDERER_PARAMS],
    [COLUMN_PINNED_FORMATTER]: [COLUMN_PINNED_FORMATTER],
    [COLUMN_FILTER]: [COLUMN_FILTER],
    [COLUMN_COMPARATOR]: [COLUMN_COMPARATOR],
    [COLUMN_PROPERTY_TYPE]: [COLUMN_PROPERTY_TYPE],
  },
  [TABLE_TYPE_AG]: {
    [COLUMN_KEY]: [COLUMN_KEY_AG],
    [COLUMN_NAME]: [COLUMN_NAME_AG],
    [COLUMN_TOOLTIP]: [COLUMN_TOOLTIP_AG],
    [COLUMN_SORTABLE]: [COLUMN_SORTABLE_AG],
    [COLUMN_WIDTH]: [COLUMN_WIDTH_AG],
    [COLUMN_AUTO_HEIGHT]: [COLUMN_AUTO_HEIGHT],
    [COLUMN_FORMATTER]: [COLUMN_FORMATTER_AG],
    [COLUMN_HEADER_FORMATTER]: [COLUMN_HEADER_FORMATTER_AG],
    [CELL_RENDERER_PARAMS]: [CELL_RENDERER_PARAMS_AG],
    [COLUMN_PINNED_FORMATTER]: [COLUMN_PINNED_FORMATTER_AG],
    [COLUMN_FILTER]: [COLUMN_FILTER],
    [COLUMN_COMPARATOR]: [COLUMN_COMPARATOR],
    [COLUMN_FILTER_PARAMS]: [COLUMN_FILTER_PARAMS],
    [COLUMN_PROPERTY_TYPE]: [COLUMN_PROPERTY_TYPE_AG],
    [COLUMN_CHILDREN]: [COLUMN_CHILDREN],
    [COLUMN_FORMATTER_FUNCTION]: [COLUMN_RENDERER_AG],
  },
};

export function enhanceColumnDefinition(
  rawData: Partial<ColDef>,
  tableType: TableType
): ColDef {
  let childrenParams;
  if (rawData && rawData[COLUMN_CHILDREN]) {
    const enhancedChildren = map(rawData[COLUMN_CHILDREN], (child) =>
      enhanceColumnDefinition(child, tableType)
    );
    childrenParams = {[COLUMN_CHILDREN]: enhancedChildren};
  }

  let formatterDefinition;
  if (
    rawData[COLUMN_FORMATTER] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_FORMATTER])
  ) {
    formatterDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_FORMATTER]
    );
  }
  let columnPinnedFormatterDefinition;
  if (
    rawData[COLUMN_PINNED_FORMATTER] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_PINNED_FORMATTER])
  ) {
    columnPinnedFormatterDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_PINNED_FORMATTER]
    );
  }
  let filterDefinition;
  if (
    rawData[COLUMN_FILTER] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_FILTER])
  ) {
    filterDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_FILTER]
    );
  }
  let comparatorDefinition;
  if (
    rawData[COLUMN_COMPARATOR] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_COMPARATOR])
  ) {
    comparatorDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_COMPARATOR]
    );
  }
  let formatterFunctionDefinition;
  if (
    rawData[COLUMN_FORMATTER_FUNCTION] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_FORMATTER_FUNCTION])
  ) {
    formatterFunctionDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_FORMATTER_FUNCTION]
    );
  }
  let filterComparatorDefinition;
  if (
    rawData[COLUMN_FILTER_PARAMS] &&
    rawData[COLUMN_FILTER_PARAMS][COLUMN_FILTER_TEXT_COMPARATOR] &&
    DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_FILTER_PARAMS][COLUMN_FILTER_TEXT_COMPARATOR]
    )
  ) {
    filterComparatorDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_FILTER_PARAMS][COLUMN_FILTER_TEXT_COMPARATOR]
    );
  }

  let headerFormatterDefinition;
  if (
    rawData[COLUMN_HEADER_FORMATTER] &&
    DynamicFormatter.getFormatterDefinition(rawData[COLUMN_HEADER_FORMATTER])
  ) {
    headerFormatterDefinition = DynamicFormatter.getFormatterDefinition(
      rawData[COLUMN_HEADER_FORMATTER]
    );
  }

  return {
    ...DEFAULT_COLUMN_PROPERTIES[tableType],
    ...omit(rawData, [
      COLUMN_GET_ROW_META_DATA,
      COLUMN_FORMATTER,
      COLUMN_PINNED_FORMATTER,
      COLUMN_FILTER,
      COLUMN_FILTER_PARAMS,
    ]),
    ...childrenParams,
    ...(tableType === TABLE_TYPE_RDG && rawData[COLUMN_GET_ROW_META_DATA]
      ? {[COLUMN_GET_ROW_META_DATA]: (row) => row}
      : {}),
    ...(formatterDefinition
      ? {
          ...formatterDefinition.columnConfig,
          [COLUMN_FORMATTER]: formatterDefinition.formatterClass,
        }
      : {}),
    ...(formatterFunctionDefinition
      ? {
          ...formatterFunctionDefinition.columnConfig,
          [COLUMN_FORMATTER_FUNCTION]:
            formatterFunctionDefinition.formatterClass,
        }
      : {}),
    ...(columnPinnedFormatterDefinition
      ? {
          ...columnPinnedFormatterDefinition.columnConfig,
          [COLUMN_PINNED_FORMATTER]:
            columnPinnedFormatterDefinition.formatterClass,
        }
      : {}),
    ...(filterDefinition
      ? {
          ...filterDefinition.columnConfig,
          [COLUMN_FILTER]: filterDefinition.formatterClass,
        }
      : {}),
    ...(comparatorDefinition
      ? {
          ...comparatorDefinition.columnConfig,
          [COLUMN_COMPARATOR]: comparatorDefinition.formatterClass,
        }
      : {}),
    ...(rawData[COLUMN_FILTER_PARAMS]
      ? {
          [COLUMN_FILTER_PARAMS]: {
            ...rawData[COLUMN_FILTER_PARAMS],
            ...(filterComparatorDefinition
              ? {
                  [COLUMN_FILTER_TEXT_COMPARATOR]:
                    filterComparatorDefinition.formatterClass,
                }
              : {}),
          },
        }
      : {}),
    ...(headerFormatterDefinition
      ? {
          ...headerFormatterDefinition.columnConfig,
          [COLUMN_HEADER_FORMATTER]: headerFormatterDefinition.formatterClass,
        }
      : {}),
  };
}

export function transformColumnDefinition(
  columnDefinition: ColDef,
  tableType: TableType
): ColDef {
  let transformedChildrenParams;
  if (columnDefinition && columnDefinition[COLUMN_CHILDREN]) {
    const enhancedChildren = map(columnDefinition[COLUMN_CHILDREN], (child) =>
      transformColumnDefinition(child, tableType)
    );
    transformedChildrenParams = {[COLUMN_CHILDREN]: enhancedChildren};
  }

  const newDefinition = {};
  const projectionKeys = keys(columnDefinition);
  for (let idx = 0, len = projectionKeys.length; idx < len; idx++) {
    const oldColumnKey = projectionKeys[idx];
    const newColumnKey = TABLE_TYPE_DEFINITION[tableType][oldColumnKey];
    newDefinition[isEmpty(newColumnKey) ? oldColumnKey : newColumnKey] =
      columnDefinition[oldColumnKey];
  }

  return {
    ...newDefinition,
    ...transformedChildrenParams,
  };
}

export function specialTableTypeModifications(
  columnDefinition: ColDef,
  tableType: TableType
): ColDef {
  let transformedChildrenParams;
  if (columnDefinition && columnDefinition[COLUMN_CHILDREN]) {
    const enhancedChildren = map(columnDefinition[COLUMN_CHILDREN], (child) =>
      specialTableTypeModifications(child, tableType)
    );
    transformedChildrenParams = {[COLUMN_CHILDREN]: enhancedChildren};
  }

  return {
    ...columnDefinition,
    ...transformedChildrenParams,
  };
}

export function getColumnDataGridProjection(
  columnRawData: Partial<ColDef>,
  tableType: TableType
): ColDef {
  // TODO: refactor transform functions
  let columnDefinition = enhanceColumnDefinition(columnRawData, tableType);
  columnDefinition = specialTableTypeModifications(columnDefinition, tableType);

  return transformColumnDefinition(columnDefinition, tableType);
}

export function getTableColumnsDataGridProjection(
  columns: ColumnConfig[] | undefined
): ColDef[] {
  const dataGridColumns: ColDef[] = [];
  if (columns) {
    forEach(columns, (column) => {
      dataGridColumns.push(
        getColumnDataGridProjection(column.getRawData(), column.getTableType())
      );
    });
  }
  return dataGridColumns;
}
